import { forwardRef, useId } from 'react';

import { cx } from 'utils';

/**
 * Input — text field for the LearnX design system.
 *
 * forwardRef so it plugs straight into React Hook Form's `register()`.
 * Composes an optional label, helper/error text, and leading/trailing
 * adornments (icons, buttons) on a frosted dark surface.
 *
 * @param {string}  [label]
 * @param {string}  [hint]      Helper text shown when there's no error.
 * @param {string}  [error]     Error message; also flips the field to danger styling.
 * @param {node}    [leftIcon]  Leading adornment.
 * @param {node}    [rightIcon] Trailing adornment.
 * @param {'sm'|'md'|'lg'} [size='md']
 */
const SIZES = {
  sm: 'h-9 text-sm rounded-lg',
  md: 'h-11 text-sm rounded-xl',
  lg: 'h-12 text-base rounded-xl',
};

const Input = forwardRef(function Input(
  {
    label,
    hint,
    error,
    leftIcon,
    rightIcon,
    size = 'md',
    className = '',
    containerClassName = '',
    id,
    ...props
  },
  ref
) {
  const autoId = useId();
  const inputId = id || autoId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  const field = cx(
    'peer w-full bg-white text-slate-900 placeholder:text-slate-400',
    // slate-300 not slate-200: a field needs a boundary you can actually see on
    // white (#E2E8F0 reads as barely-there at 1.2:1).
    'border border-slate-300 transition-all duration-200 ease-premium',
    'hover:border-slate-400',
    'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25',
    'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-60',
    SIZES[size] || SIZES.md,
    leftIcon ? 'pl-10' : 'pl-4',
    rightIcon ? 'pr-10' : 'pr-4',
    error && 'border-danger focus:border-danger focus:ring-danger/25',
    className
  );

  return (
    <div className={cx('w-full', containerClassName)}>
      {label && (
        <label htmlFor={inputId} className="mb-2 block text-sm font-semibold text-slate-800">
          {label}
        </label>
      )}

      <div className="relative">
        {leftIcon && (
          <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-slate-400 peer-focus:text-brand-600">
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={field}
          {...props}
        />
        {rightIcon && (
          <span className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400">
            {rightIcon}
          </span>
        )}
      </div>

      {error ? (
        <p id={`${inputId}-error`} className="mt-1.5 text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="mt-1.5 text-xs text-slate-500">
          {hint}
        </p>
      ) : null}

    </div>
  );
});

export default Input;
